import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { trackEvent } from "../lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

const Block = ({ block }) => {
  if (block.type === "h2")
    return (
      <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink mt-14 mb-5 leading-tight">
        {block.text}
      </h2>
    );
  if (block.type === "quote")
    return (
      <blockquote className="my-12 border-l-2 border-teal pl-6 md:pl-10 font-serif italic text-2xl md:text-3xl text-forest leading-snug max-w-2xl">
        {block.text}
      </blockquote>
    );
  if (block.type === "image")
    return (
      <figure className="my-10 md:my-14">
        <div className="clip-frame bg-sand-dark">
          <img src={block.url} alt={block.text || "Article image"} className="w-full h-auto object-cover" loading="lazy" />
        </div>
        {block.text && (
          <figcaption className="mt-3 text-xs md:text-sm text-stone leading-relaxed max-w-2xl">
            {block.text}
          </figcaption>
        )}
      </figure>
    );
  return (
    <p className="text-ink/85 text-base md:text-lg leading-[1.75] mb-5 max-w-2xl">
      {block.text}
    </p>
  );
};

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/insights/${slug}`)
      .then((r) => {
        setPost(r.data);
        trackEvent("insight_view", { slug: r.data.slug, title: r.data.title, category: r.data.category });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-serif text-4xl text-ink">Article not found</p>
        <Link to="/insights" className="link-underline text-xs uppercase tracking-[0.2em] font-bold text-teal">
          Back to Insights
        </Link>
      </div>
    );

  if (!post)
    return <div className="min-h-screen flex items-center justify-center text-stone">Loading…</div>;

  const url = typeof window !== "undefined" ? window.location.href : "";

  return (
    <article data-testid="insight-detail-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title={post.title}
        description={post.excerpt}
        image={post.og_image || post.cover_image}
        path={`/insights/${post.slug}`}
        type="article"
        keywords={post.keywords || []}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.og_image || post.cover_image,
          datePublished: post.published_date,
          dateModified: post.published_date,
          author: {
            "@type": "Person",
            name: post.author_name || "Janani S",
            jobTitle: "Architect & Researcher",
            worksFor: { "@type": "Organization", name: "Blue Green Studio" },
          },
          publisher: {
            "@type": "Organization",
            name: "Blue Green Studio",
            logo: {
              "@type": "ImageObject",
              url: `${typeof window !== "undefined" ? window.location.origin : ""}/logo192.png`,
            },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          keywords: (post.keywords || []).join(", "),
          articleSection: post.category,
        }}
      />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Link
          to="/insights"
          data-testid="back-to-insights"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-stone hover:text-teal transition-colors mb-10"
        >
          <ArrowLeft size={16} /> Back to Insights
        </Link>

        <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-6">
          {post.category}
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="font-serif text-4xl sm:text-5xl lg:text-[4.5vw] leading-[1] tracking-tight text-ink max-w-5xl"
        >
          {post.title}
        </motion.h1>
        <p className="mt-6 text-xs uppercase tracking-[0.2em] text-stone">
          {new Date(post.published_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · {post.read_time}
        </p>
        <div className="mt-8 flex items-center gap-4" data-testid="article-byline">
          <img
            src={post.author_image}
            alt={post.author_name || "Author"}
            className="w-11 h-11 rounded-full object-cover border border-ink/10"
          />
          <div className="text-left">
            <p className="text-sm font-bold text-ink" data-testid="article-author-name">{post.author_name || "Janani S"}</p>
            <p className="text-xs text-stone">Architect & Researcher — Sustainability and Inclusive Urbanism</p>
          </div>
        </div>
      </div>

      <Reveal className="max-w-[1600px] mx-auto px-6 md:px-12 mt-12 md:mt-16">
        <div className="clip-frame aspect-[16/8] bg-sand-dark">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </Reveal>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-16 md:mt-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8 md:col-start-3">
            {post.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}

            <div className="mt-16 pt-10 border-t border-ink/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <p className="text-stone text-sm max-w-md">
                Working on a home, an interior fit-out or an alternative-construction project? We'd love to hear about it.
              </p>
              <Link
                to="/quiz"
                className="bg-forest text-sand px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-teal transition-colors duration-500"
              >
                Take the Quiz
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
